import React, { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Settings, Users, MessageSquare, Activity, Send, Zap,
  RefreshCw, Eye, EyeOff, Save, Search, Webhook, Phone, Trash2,
  CheckCircle2, AlertCircle, Loader2, ExternalLink,
} from "lucide-react";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

const TABS = [
  { id: "config",        label: "Configuración",  icon: Settings },
  { id: "team",          label: "For Your Team",  icon: Users },
  { id: "conversations", label: "Conversaciones", icon: MessageSquare },
  { id: "status",        label: "Estado",         icon: Activity },
  { id: "test",          label: "Probar",         icon: Send },
  { id: "logs",          label: "Eventos",        icon: Zap },
];

function StatusBadge({ status }) {
  const map = {
    connected:     { cls: "bg-green-500/20 text-green-300 border-green-500/30", label: "Conectado", Icon: CheckCircle2 },
    configured:    { cls: "bg-green-500/20 text-green-300 border-green-500/30", label: "Configurado", Icon: CheckCircle2 },
    not_configured:{ cls: "bg-slate-700/40 text-slate-300 border-slate-600",    label: "Sin configurar", Icon: AlertCircle },
    auth_error:    { cls: "bg-red-500/20 text-red-300 border-red-500/30",       label: "API key inválida", Icon: AlertCircle },
    error:         { cls: "bg-red-500/20 text-red-300 border-red-500/30",       label: "Error", Icon: AlertCircle },
    timeout:       { cls: "bg-yellow-500/20 text-yellow-300 border-yellow-500/30", label: "Timeout", Icon: AlertCircle },
  };
  const m = map[status] || map.error;
  const I = m.Icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-xs ${m.cls}`}>
      <I className="w-3 h-3" /> {m.label}
    </span>
  );
}

export default function KapsoServerPanel({ getAdminHeaders }) {
  const [tab, setTab] = useState("config");
  const [status, setStatus] = useState(null);

  const headers = useCallback(() => ({
    "Content-Type": "application/json",
    ...(getAdminHeaders ? getAdminHeaders() : {}),
  }), [getAdminHeaders]);

  const fetchStatus = useCallback(async () => {
    try {
      const r = await fetch(`${API}/admin/kapso/status`, { headers: headers(), credentials: "include" });
      if (r.ok) setStatus(await r.json());
    } catch (_) {}
  }, [headers]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-green-500/20 border border-green-500/40 flex items-center justify-center">
            <Phone className="w-5 h-5 text-green-300" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Kapso Server</h1>
            <p className="text-sm text-slate-400">WhatsApp Business para OkChat</p>
          </div>
          {status && <StatusBadge status={status.status} />}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={fetchStatus} className="px-3 py-2 rounded bg-slate-700/60 hover:bg-slate-700 text-slate-200 inline-flex items-center gap-2">
            <RefreshCw className="w-4 h-4" /> Refrescar
          </button>
          <a href="https://docs.kapso.ai/docs/introduction" target="_blank" rel="noreferrer" className="px-3 py-2 rounded bg-slate-700/60 hover:bg-slate-700 text-slate-200 inline-flex items-center gap-2">
            <ExternalLink className="w-4 h-4" /> Docs
          </a>
        </div>
      </div>

      <div className="flex flex-wrap gap-1 border-b border-slate-700">
        {TABS.map((t) => {
          const I = t.icon;
          const active = tab === t.id;
          return (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`px-3 py-2 text-sm rounded-t inline-flex items-center gap-2 border-b-2 ${active
                ? "bg-green-500/20 text-green-300 border-green-500/30"
                : "text-slate-400 border-transparent hover:text-white"}`}>
              <I className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>

      <div className="bg-slate-900/50 border border-slate-700 rounded-lg p-5">
        {tab === "config"        && <ConfigSection headers={headers} onSaved={fetchStatus} />}
        {tab === "team"          && <TeamSection headers={headers} />}
        {tab === "conversations" && <ConversationsSection headers={headers} />}
        {tab === "status"        && <StatusSection headers={headers} status={status} refresh={fetchStatus} />}
        {tab === "test"          && <TestSection headers={headers} />}
        {tab === "logs"          && <LogsSection headers={headers} />}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- Config

function ConfigSection({ headers, onSaved }) {
  const [cfg, setCfg] = useState({ api_key: "", phone_number_id: "", whatsapp_number: "",
    webhook_url: "", auto_reply: true, is_active: true, linked_agent_id: "" });
  const [agents, setAgents] = useState([]);
  const [showKey, setShowKey] = useState(false);
  const [saving, setSaving] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const [numbers, setNumbers] = useState(null);
  const [registering, setRegistering] = useState(false);

  useEffect(() => { (async () => {
    const r = await fetch(`${API}/admin/kapso/config`, { headers: headers(), credentials: "include" });
    if (r.ok) { const j = await r.json(); if (j.config) setCfg((c) => ({ ...c, ...j.config })); }
    const ra = await fetch(`${API}/admin/ai-agents`, { headers: headers(), credentials: "include" });
    if (ra.ok) { const ja = await ra.json(); setAgents(Array.isArray(ja) ? ja : (ja.agents || [])); }
    const guess = `${window.location.origin}/api/admin/kapso/webhook/receive`;
    setCfg((c) => ({ ...c, webhook_url: c.webhook_url || guess }));
  })(); }, [headers]);

  const update = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API}/admin/kapso/config`, {
        method: "POST", headers: headers(), credentials: "include", body: JSON.stringify(cfg),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || "Error al guardar");
      toast.success("Configuración guardada");
      if (j.config) setCfg((c) => ({ ...c, ...j.config }));
      onSaved && onSaved();
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  const detect = async () => {
    setDetecting(true); setNumbers(null);
    try {
      const r = await fetch(`${API}/admin/kapso/phone-numbers`, { headers: headers(), credentials: "include" });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || "Error");
      setNumbers(j.phone_numbers || []);
      if ((j.phone_numbers || []).length === 0) toast.info("No se encontraron números en la cuenta Kapso");
    } catch (e) { toast.error(e.message); }
    setDetecting(false);
  };

  const pickNumber = (n) => {
    setCfg((c) => ({ ...c, phone_number_id: n.id, whatsapp_number: n.number || c.whatsapp_number }));
    setNumbers(null); toast.success(`Seleccionado: ${n.number || n.id}`);
  };

  const registerWebhook = async () => {
    if (!cfg.webhook_url) return toast.error("Falta webhook_url");
    setRegistering(true);
    try {
      const r = await fetch(`${API}/admin/kapso/webhook/setup?webhook_url=${encodeURIComponent(cfg.webhook_url)}`, {
        method: "POST", headers: headers(), credentials: "include",
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || "Error");
      toast.success(`Webhook registrado (${j.status_code})`);
    } catch (e) { toast.error(e.message); }
    setRegistering(false);
  };

  return (
    <div className="space-y-4">
      <div className="bg-blue-500/10 border border-blue-500/30 rounded p-3 text-sm text-blue-200">
        Usa la API de <a className="underline" href="https://docs.kapso.ai/docs/whatsapp/personal-agent" target="_blank" rel="noreferrer">Personal Agent</a> de Kapso.
        Header de auth: <code>X-API-Key</code>. Encuentra tu <em>phone_number_id</em> con el botón "Detectar Números".
      </div>

      <Field label="API Key">
        <div className="flex gap-2">
          <input type={showKey ? "text" : "password"} value={cfg.api_key || ""}
            onChange={(e) => update("api_key", e.target.value)}
            placeholder="kapso-..." className="input flex-1" />
          <button onClick={() => setShowKey((s) => !s)} className="px-3 rounded bg-slate-700">
            {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
      </Field>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Phone Number ID">
          <div className="flex gap-2">
            <input value={cfg.phone_number_id || ""} onChange={(e) => update("phone_number_id", e.target.value)}
              placeholder="123456789012345" className="input flex-1" />
            <button onClick={detect} disabled={detecting} className="px-3 rounded bg-slate-700 inline-flex items-center gap-2">
              {detecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              Detectar
            </button>
          </div>
        </Field>
        <Field label="Número WhatsApp (display)">
          <input value={cfg.whatsapp_number || ""} onChange={(e) => update("whatsapp_number", e.target.value)}
            placeholder="+52 1 555 555 5555" className="input w-full" />
        </Field>
      </div>

      {numbers && numbers.length > 0 && (
        <div className="bg-slate-800/60 border border-slate-700 rounded p-2 space-y-1">
          {numbers.map((n) => (
            <button key={n.id} onClick={() => pickNumber(n)}
              className="w-full text-left px-3 py-2 rounded hover:bg-slate-700/60 text-sm text-slate-200">
              <span className="font-medium">{n.number || "(sin número)"}</span>
              <span className="text-slate-400"> — {n.name || ""} · id: {n.id}</span>
            </button>
          ))}
        </div>
      )}

      <Field label="Webhook URL">
        <div className="flex gap-2">
          <input value={cfg.webhook_url || ""} onChange={(e) => update("webhook_url", e.target.value)} className="input flex-1" />
          <button onClick={registerWebhook} disabled={registering} className="px-3 rounded bg-slate-700 inline-flex items-center gap-2">
            {registering ? <Loader2 className="w-4 h-4 animate-spin" /> : <Webhook className="w-4 h-4" />}
            Registrar
          </button>
        </div>
      </Field>

      <Field label="Agente IA vinculado">
        <select value={cfg.linked_agent_id || ""} onChange={(e) => update("linked_agent_id", e.target.value)} className="input w-full">
          <option value="">main (orquestador por defecto)</option>
          {agents.map((a) => <option key={a.agent_id} value={a.agent_id}>{a.name} — {a.description?.slice(0, 40)}</option>)}
        </select>
      </Field>

      <div className="flex items-center gap-6">
        <Toggle label="Auto-respuesta activa" value={!!cfg.auto_reply} onChange={(v) => update("auto_reply", v)} />
        <Toggle label="Módulo activo" value={!!cfg.is_active} onChange={(v) => update("is_active", v)} />
      </div>

      <div className="pt-2">
        <button onClick={save} disabled={saving} className="px-4 py-2 rounded bg-green-600 hover:bg-green-500 text-white inline-flex items-center gap-2">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Guardar configuración
        </button>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-xs uppercase tracking-wide text-slate-400 mb-1">{label}</div>
      {children}
    </label>
  );
}

function Toggle({ label, value, onChange }) {
  return (
    <label className="inline-flex items-center gap-2 text-sm text-slate-200 cursor-pointer">
      <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="accent-green-500 w-4 h-4" />
      {label}
    </label>
  );
}

// ---------------------------------------------------------------- Status

function StatusSection({ headers, status, refresh }) {
  useEffect(() => {
    const id = setInterval(refresh, 15000);
    return () => clearInterval(id);
  }, [refresh]);
  if (!status) return <div className="text-slate-400">Cargando…</div>;
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <StatusBadge status={status.status} />
        <button onClick={refresh} className="text-sm text-slate-300 inline-flex items-center gap-1 hover:text-white">
          <RefreshCw className="w-4 h-4" /> Re-verificar
        </button>
      </div>
      <pre className="bg-slate-950 border border-slate-700 rounded p-3 text-xs text-slate-300 overflow-x-auto">
        {JSON.stringify(status, null, 2)}
      </pre>
    </div>
  );
}

// ---------------------------------------------------------------- Team

function TeamSection({ headers }) {
  const [cfg, setCfg] = useState({ team_name: "", inbox_id: "", description: "", auto_assignment: true, max_conversations_per_agent: 10 });
  const [saving, setSaving] = useState(false);
  useEffect(() => { (async () => {
    const r = await fetch(`${API}/admin/kapso/team/config`, { headers: headers(), credentials: "include" });
    if (r.ok) { const j = await r.json(); if (j.config) setCfg(j.config); }
  })(); }, [headers]);
  const update = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API}/admin/kapso/team/configure`, {
        method: "POST", headers: headers(), credentials: "include", body: JSON.stringify(cfg),
      });
      if (!r.ok) throw new Error("Error");
      toast.success("Equipo guardado");
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };
  return (
    <div className="space-y-4">
      <div className="text-sm text-blue-200 bg-blue-500/10 border border-blue-500/30 rounded p-3">
        Configura el equipo destino — ver <a className="underline" href="https://docs.kapso.ai/docs/platform/for-your-team" target="_blank" rel="noreferrer">docs</a>.
      </div>
      <Field label="Nombre del equipo"><input className="input w-full" value={cfg.team_name} onChange={(e) => update("team_name", e.target.value)} /></Field>
      <Field label="Inbox ID"><input className="input w-full" value={cfg.inbox_id} onChange={(e) => update("inbox_id", e.target.value)} /></Field>
      <Field label="Descripción"><textarea className="input w-full" rows={2} value={cfg.description || ""} onChange={(e) => update("description", e.target.value)} /></Field>
      <div className="flex items-center gap-6">
        <Toggle label="Asignación automática" value={!!cfg.auto_assignment} onChange={(v) => update("auto_assignment", v)} />
        <Field label="Máx conv. por agente">
          <input type="number" min={1} max={100} className="input w-24" value={cfg.max_conversations_per_agent || 10}
            onChange={(e) => update("max_conversations_per_agent", parseInt(e.target.value || "10", 10))} />
        </Field>
      </div>
      <button onClick={save} disabled={saving} className="px-4 py-2 rounded bg-green-600 hover:bg-green-500 text-white inline-flex items-center gap-2">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar
      </button>
    </div>
  );
}

// ---------------------------------------------------------------- Test

function TestSection({ headers }) {
  const [to, setTo] = useState(""); const [message, setMessage] = useState("Prueba desde OkChat");
  const [mediaUrl, setMediaUrl] = useState(""); const [mediaType, setMediaType] = useState("");
  const [sending, setSending] = useState(false); const [resp, setResp] = useState(null);
  const send = async () => {
    setSending(true); setResp(null);
    try {
      const r = await fetch(`${API}/admin/kapso/message/send`, {
        method: "POST", headers: headers(), credentials: "include",
        body: JSON.stringify({ to, message, media_url: mediaUrl || null, media_type: mediaType || null }),
      });
      const j = await r.json(); setResp(j);
      if (r.ok && (j.status_code === 200 || j.status_code === 201)) toast.success("Mensaje enviado"); else toast.error(`HTTP ${j.status_code || r.status}`);
    } catch (e) { toast.error(e.message); }
    setSending(false);
  };
  return (
    <div className="space-y-3">
      <Field label="Destino (E.164)"><input className="input w-full" placeholder="+5215555555555" value={to} onChange={(e) => setTo(e.target.value)} /></Field>
      <Field label="Mensaje"><textarea rows={3} className="input w-full" value={message} onChange={(e) => setMessage(e.target.value)} /></Field>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Field label="Media URL (opcional)"><input className="input w-full" value={mediaUrl} onChange={(e) => setMediaUrl(e.target.value)} /></Field>
        <Field label="Media Type">
          <select className="input w-full" value={mediaType} onChange={(e) => setMediaType(e.target.value)}>
            <option value="">(ninguno)</option>
            <option value="image">image</option><option value="video">video</option>
            <option value="document">document</option><option value="audio">audio</option>
          </select>
        </Field>
      </div>
      <button onClick={send} disabled={sending || !to || !message} className="px-4 py-2 rounded bg-green-600 hover:bg-green-500 text-white inline-flex items-center gap-2">
        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Enviar
      </button>
      {resp && <pre className="bg-slate-950 border border-slate-700 rounded p-3 text-xs text-slate-300 overflow-x-auto">{JSON.stringify(resp, null, 2)}</pre>}
    </div>
  );
}

// ---------------------------------------------------------------- Conversations

function ConversationsSection({ headers }) {
  const [list, setList] = useState([]); const [active, setActive] = useState(null); const [msgs, setMsgs] = useState([]);
  const reload = useCallback(async () => {
    const r = await fetch(`${API}/admin/kapso/whatsapp/conversations`, { headers: headers(), credentials: "include" });
    if (r.ok) setList((await r.json()).conversations || []);
  }, [headers]);
  useEffect(() => { reload(); }, [reload]);
  const open = async (phone) => {
    setActive(phone);
    const r = await fetch(`${API}/admin/kapso/whatsapp/conversation/${encodeURIComponent(phone)}`, { headers: headers(), credentials: "include" });
    if (r.ok) setMsgs((await r.json()).messages || []);
  };
  const del = async () => {
    if (!active) return; if (!window.confirm(`¿Borrar historial de ${active}?`)) return;
    const r = await fetch(`${API}/admin/kapso/whatsapp/conversation/${encodeURIComponent(active)}`, { method: "DELETE", headers: headers(), credentials: "include" });
    if (r.ok) { toast.success("Borrado"); setActive(null); setMsgs([]); reload(); }
  };
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 min-h-[400px]">
      <div className="md:col-span-1 space-y-1 max-h-[60vh] overflow-y-auto">
        {list.length === 0 && <div className="text-slate-400 text-sm">Sin conversaciones aún.</div>}
        {list.map((c) => (
          <button key={c.phone} onClick={() => open(c.phone)}
            className={`w-full text-left px-3 py-2 rounded ${active === c.phone ? "bg-green-500/15 border border-green-500/30" : "hover:bg-slate-800/60 border border-transparent"}`}>
            <div className="text-sm text-white font-medium">{c.phone}</div>
            <div className="text-xs text-slate-400 truncate">{c.last_role}: {c.last_message?.slice(0, 80)}</div>
          </button>
        ))}
      </div>
      <div className="md:col-span-2 bg-slate-950/50 border border-slate-700 rounded p-3 max-h-[60vh] overflow-y-auto">
        {!active && <div className="text-slate-400 text-sm">Selecciona una conversación.</div>}
        {active && (
          <>
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm text-white">{active}</div>
              <button onClick={del} className="text-red-400 hover:text-red-300 inline-flex items-center gap-1 text-sm"><Trash2 className="w-4 h-4" /> Borrar</button>
            </div>
            <div className="space-y-2">
              {msgs.map((m, i) => (
                <div key={i} className={`max-w-[80%] px-3 py-2 rounded ${m.role === "user" ? "bg-slate-800 text-slate-100" : "bg-green-600/20 text-green-100 ml-auto"}`}>
                  <div className="text-[10px] text-slate-400 mb-1">{m.role} · {new Date(m.created_at).toLocaleString()}</div>
                  <div className="text-sm whitespace-pre-wrap">{m.content}</div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- Logs

function LogsSection({ headers }) {
  const [events, setEvents] = useState([]); const [open, setOpen] = useState({});
  const reload = useCallback(async () => {
    const r = await fetch(`${API}/admin/kapso/logs?limit=50`, { headers: headers(), credentials: "include" });
    if (r.ok) setEvents((await r.json()).events || []);
  }, [headers]);
  useEffect(() => { reload(); }, [reload]);
  return (
    <div className="space-y-2">
      <div className="flex justify-end">
        <button onClick={reload} className="text-sm text-slate-300 inline-flex items-center gap-1 hover:text-white"><RefreshCw className="w-4 h-4" /> Refrescar</button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="text-slate-400">
            <tr><th className="text-left p-2">Hora</th><th className="text-left p-2">Evento</th><th className="text-left p-2">Tel.</th>
              <th className="text-left p-2">Mensaje</th><th className="text-left p-2">Resp.</th><th className="text-left p-2">OK</th><th></th></tr>
          </thead>
          <tbody>
            {events.map((e, i) => (
              <React.Fragment key={i}>
                <tr className="border-t border-slate-800 hover:bg-slate-800/30">
                  <td className="p-2 text-slate-300">{e.received_at?.slice(11, 19)}</td>
                  <td className="p-2 text-slate-300">{e.event_type}</td>
                  <td className="p-2 text-slate-300">{e.phone || "—"}</td>
                  <td className="p-2 text-slate-200 max-w-[18rem] truncate">{e.message_preview || e.skip_reason || ""}</td>
                  <td className="p-2 text-slate-200 max-w-[18rem] truncate">{e.reply_preview || ""}</td>
                  <td className="p-2">{e.processed ? <span className="text-green-400">✓</span> : <span className="text-red-400">✗</span>}</td>
                  <td className="p-2"><button onClick={() => setOpen((o) => ({ ...o, [i]: !o[i] }))} className="text-slate-400 hover:text-white">{open[i] ? "−" : "+"}</button></td>
                </tr>
                {open[i] && (
                  <tr><td colSpan={7} className="p-2 bg-slate-950">
                    <pre className="text-[10px] text-slate-400 overflow-x-auto">{JSON.stringify(e.payload, null, 2)}</pre>
                    {e.error && <div className="text-red-400 text-xs mt-1">Error: {e.error}</div>}
                  </td></tr>
                )}
              </React.Fragment>
            ))}
            {events.length === 0 && <tr><td colSpan={7} className="p-4 text-center text-slate-500">Sin eventos aún.</td></tr>}
          </tbody>
        </table>
      </div>
      <style>{`
        .input { background: rgb(15 23 42 / .8); border: 1px solid rgb(51 65 85); color: white; padding: 0.5rem 0.75rem; border-radius: 0.375rem; outline: none; }
        .input:focus { border-color: rgb(34 197 94); }
      `}</style>
    </div>
  );
}
